/* eslint-disable camelcase */
/* eslint-disable react/jsx-max-depth */
import React from "react";
import DOMPurify from "dompurify";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { LoginData } from "../../../models/userData";
import { login } from "../../../redux/auth/actions";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux";
import { getCurrentLanguage } from "../../../models/enums/salutationType.enum";
import CustomTextField from "../../../components/forms/CustomTextField";
import routes from "../../../routing/routes";
import "./LoginPage.scss";

/**
 * @component LoginPage
 *
 * This component renders the login page with a form for user authentication.
 * It includes fields for username and password, a submit button, and links for registration and password recovery.
 * The page also displays a customer-specific login text.
 *
 * @returns LoginPage component
 */
export const LoginPage: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    metaData: { metaData, metaDataLoaded },
    featureFlagging: { featureFlags },
  } = useSelector((state: AppState) => state);

  const { t } = useTranslation(["authentication", "common", "layout", "snackbars"]);

  // !NOTE: for svg use fixed width
  const { from } = (location.state as { from: { pathname: string } }) || {
    from: { pathname: routes.login },
  };

  const loginText =
    getCurrentLanguage() === "de" || getCurrentLanguage() === "de-DE"
      ? {
          text: metaData?.login_text_german,
          username: metaData?.login_username_german,
        }
      : {
          text: metaData?.login_text_english,
          username: metaData?.login_username_english,
        };

  const createSafeHTML = (htmlString: string) => {
    return { __html: DOMPurify.sanitize(htmlString) };
  };

  const loginContent = (
    <Box
      className="login-content"
      style={{ padding: 0, margin: 0, overflowWrap: "anywhere" }}
    >
      {/* eslint-disable-next-line react/no-danger */}
      <p dangerouslySetInnerHTML={createSafeHTML(loginText.text)} />
    </Box>
  );

  if (!metaDataLoaded) return <CircularProgress />;

  return (
    <Grid container alignItems="center" style={{ marginTop: "1.25rem" }}>
      <Grid item xs={12} md={9} style={{ paddingBottom: ".375rem" }}>
        <Typography>{loginContent}</Typography>
      </Grid>
      <Grid item xs={12} md={9}>
        <Box>
          <Formik
            onSubmit={(data: LoginData) => {
              dispatch(login(data, from));
            }}
            initialValues={{ username: "", password: "" }}
          >
            <Form
              className={classes.loginForm}
              style={{ marginTop: "1.25rem" }}
              data-testid="login-form"
            >
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <CustomTextField
                      autoComplete="username"
                      name="username"
                      id="profile-mail"
                      label={loginText.username}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <CustomTextField
                      autoComplete="current-password"
                      name="password"
                      id="profile-password"
                      type="password"
                      label={t("profile.password", { ns: "common" })}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Button
                      id="submit-login"
                      type="submit"
                      size="large"
                      variant="contained"
                      color="primary"
                      disableElevation
                      disableRipple
                    >
                      {t("login", { ns: "authentication" })}
                    </Button>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                {featureFlags?.registration_is_enabled ? (
                  <Grid item xs={12} sm={6}>
                    <Link
                      className="icon-arrow-right large-link"
                      href="/registration"
                      rel="noopener noreferrer"
                    >
                      {t("loginPage.register", { ns: "authentication" })}
                    </Link>
                  </Grid>
                ) : null}
                {featureFlags?.forgot_password_is_enabled ? (
                  <Grid item xs={12} sm={6}>
                    <Link
                      href={routes.forgotPassword}
                      className="icon-arrow-right large-link"
                    >
                      {t("password.forgotten.title", {
                        ns: "authentication",
                      })}
                    </Link>
                  </Grid>
                ) : null}
              </Grid>
            </Form>
          </Formik>
        </Box>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  formGrid: {
    paddingLeft: ".7031rem",
    paddingRight: ".7031rem",
  },
  margin: {
    margin: theme.spacing(1),
  },
  loginForm: {
    "& > *": {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      width: "100%",
    },
  },
}));
